import React, { FC, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { MultiStepDialog } from '@modernloop/shared/components';
import _, { noop } from 'lodash';

import { SelfScheduleLocation, useTaskShareDialogQuery } from 'src/generated/mloop-graphql';

import ShareDialog, { CreateType } from 'src/components/Dialogs/ShareDialog';

import { useCreateLocation } from 'src/hooks/amplitude/useCreateLocationHeader';
import { LoggerEvent, useLogEvent } from 'src/hooks/useLogEvent';

import { useCandidatePortalUrl } from 'src/urls/hooks/useCandidatePortalUrl';

import CandidateAvailabilityEmailPreview from 'src/views-new/CandidateAvailabilityOptions/ShareOptions';
import SelfScheduleEmail from 'src/views-new/SelfSchedule/SelfScheduleEmail';
import useSelfScheduleOptionsUpsert from 'src/views-new/SelfSchedule/useSelfScheduleOptionsUpsert';

interface Props {
  taskId: string;
  type: CreateType;
  onClose: () => void;
}

const TaskShareDialog: FC<Props> = ({ taskId, type, onClose }) => {
  const { t } = useTranslation();
  const { data, loading } = useTaskShareDialogQuery({
    variables: {
      taskId,
    },
  });

  const jobStageSettingsV2 = data?.task?.jobStage?.jobStageSettingsV2;
  const { candidatePortalUrl } = useCandidatePortalUrl(data?.task?.application?.id, { taskId });

  const { logEvent } = useLogEvent();
  const createLocation = useCreateLocation();

  const [isAvailabilitySendModalOpen, setIsAvailabilitySendModalOpen] = useState(false);
  const [isSelfScheduleModalOpen, setIsSelfScheduleModalOpen] = useState(false);

  const candidate = data?.task?.application.candidate;

  const availabilityRequest = _.first(data?.task?.availabilityRequests);
  const selfScheduleRequest = _.first(data?.task?.selfScheduleRequests);

  // Handle self schedule options upsert
  const selfScheduleOptionsUpsert = useSelfScheduleOptionsUpsert(
    {
      taskId,
      candidateEmailTemplateId: selfScheduleRequest?.candidateEmailTemplateId,
      candidateEventTemplateId: selfScheduleRequest?.candidateEventTemplateId,
      interviewerEventTemplateId: selfScheduleRequest?.interviewerEventTemplateId,
      candidateEmailContent: {
        toEmailAddress: '',
        description: '',
        summary: '',
      },
      options: {
        candidateCalendarId: selfScheduleRequest?.options?.candidateCalendarId || '',
        interviewerCalendarId: selfScheduleRequest?.options?.interviewerCalendarId || '',
        location: selfScheduleRequest?.options?.location || SelfScheduleLocation.None,
        inclusionDays: selfScheduleRequest?.options?.inclusionDays,
        advanceNoticeInHours: selfScheduleRequest?.options?.advanceNoticeInHours,
        rollingDays: selfScheduleRequest?.options?.rollingDays,
        zoomHost: selfScheduleRequest?.options?.zoomHost,
        zoomHostUserId: selfScheduleRequest?.options?.zoomHostUserId,
        shouldRespectLoadLimit: selfScheduleRequest?.options?.shouldRespectLoadLimit,
        canScheduleOverAvailableKeywords: selfScheduleRequest?.options?.canScheduleOverAvailableKeywords,
        canScheduleOverFreeTime: selfScheduleRequest?.options?.canScheduleOverFreeTime,
        canScheduleOverRecruitingKeywords: selfScheduleRequest?.options?.canScheduleOverRecruitingKeywords,
      },
    },
    taskId
  );

  const longUrl = candidatePortalUrl;

  if (loading) {
    return null;
  }

  if (!data?.task) {
    return null;
  }

  const handleAvailabilityClose = () => {
    setIsAvailabilitySendModalOpen(false);
    onClose();
  };

  const handleSelfScheduleClose = () => {
    setIsSelfScheduleModalOpen(false);
    onClose();
  };

  return (
    <>
      {(!isAvailabilitySendModalOpen || !isSelfScheduleModalOpen) && (
        <ShareDialog
          longUrl={longUrl}
          content={t('hiring-dashboard-candidates:candidates.taskShareDialog.uniqueTaskLinkForCandidate', {
            candidateName: candidate?.fullName ?? '',
          })}
          onClose={onClose}
          onSendEmail={() => {
            if (type === CreateType.CANDIDATE_AVAILABILITY) {
              setIsAvailabilitySendModalOpen(true);
            } else {
              setIsSelfScheduleModalOpen(true);
            }
            logEvent(LoggerEvent.CLIENT_SHARE_DIALOG_SEND_EMAIL, {
              type,
              location: createLocation,
            });
          }}
        />
      )}
      {availabilityRequest && isAvailabilitySendModalOpen && jobStageSettingsV2 && (
        <MultiStepDialog open maxWidth="sm" onClose={onClose}>
          <CandidateAvailabilityEmailPreview
            jobStageSettingsV2={jobStageSettingsV2}
            taskId={taskId}
            options={{
              id: availabilityRequest.id,
              applicationId: availabilityRequest.applicationId,
              jobStageId: availabilityRequest.jobStageId,
              taskId,
              numberOfDays: availabilityRequest.numberOfDays,
              minutesPerDays: availabilityRequest.minutesPerDays,
              minimumTimeBlockMinutes: availabilityRequest.minimumTimeBlockMinutes,
              timezone: availabilityRequest.timezone,
              availabilityStartDate: availabilityRequest.availabilityStartDate,
              availabilityEndDate: availabilityRequest.availabilityEndDate,
              advanceNoticeMinutes: availabilityRequest.advanceNoticeMinutes,
              candidateNote: availabilityRequest.candidateNote || undefined,
              externalId: availabilityRequest.externalId,
              createdAt: availabilityRequest.createdAt,
              updatedAt: availabilityRequest.updatedAt,
              lastCandidateCommunicationSentAt: availabilityRequest.lastCandidateCommunicationSentAt,
              // This just allows the below boolean options to be set to their values when saving
              isValidInterviewPlan: true,
              shouldRespectLoadLimit: availabilityRequest.shouldRespectLoadLimit,
              bookIfPerfectScheduleFound: availabilityRequest.bookIfPerfectScheduleFound,
              canScheduleOverAvailableKeywords: availabilityRequest.suggestOverAvailableKeywords,
              canScheduleOverFreeTime: availabilityRequest.suggestOverFreeTime,
              canScheduleOverRecruitingKeywords: availabilityRequest.suggestOverRecruitingKeywords,
              useRollingDays: Boolean(availabilityRequest.rollingDays),
              timeframeNumberOfDays: availabilityRequest.rollingDays,
              inclusionDays: availabilityRequest.inclusionDays,
            }}
            onSaveSuccess={handleAvailabilityClose}
            onClose={handleAvailabilityClose}
            applicationId={availabilityRequest.applicationId}
            candidateId={candidate?.id}
            jobStageId={availabilityRequest.jobStageId}
            onOptionsChanged={noop}
          />
        </MultiStepDialog>
      )}
      {selfScheduleRequest && isSelfScheduleModalOpen && jobStageSettingsV2 && (
        <MultiStepDialog open maxWidth="sm" onClose={onClose}>
          <SelfScheduleEmail
            jobStageSettingsV2={jobStageSettingsV2}
            taskId={taskId}
            applicationId={selfScheduleRequest.applicationId}
            candidateId={candidate?.id || ''}
            jobStageId={selfScheduleRequest.jobStageId}
            selfScheduleOptionsUpsert={selfScheduleOptionsUpsert}
            onSave={handleSelfScheduleClose}
            onCancel={handleSelfScheduleClose}
          />
        </MultiStepDialog>
      )}
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TASK_SHARE_DIALOG_QUERY = gql`
  ${SelfScheduleEmail.fragments.jobStageSettingsV2}
  ${CandidateAvailabilityEmailPreview.fragments.jobStageSettingsV2}
  query TaskShareDialog($taskId: uuid!) {
    task(id: $taskId) {
      id
      jobStage {
        id
        jobStageSettingsV2 {
          ...CandidateAvailabilityEmailPreview_jobStageSettingsV2
          ...SelfScheduleEmail_jobStageSettingsV2
        }
      }
      application {
        id
        jobId

        candidate {
          id
          fullName
        }
      }
      availabilityRequests {
        id
        applicationId
        availabilityEndDate
        availabilityStartDate
        candidateNote
        externalId
        createdAt
        jobStageId
        minimumTimeBlockMinutes
        minutesPerDays
        numberOfDays
        timezone
        updatedAt
        lastCandidateCommunicationSentAt
        advanceNoticeMinutes
        shouldRespectLoadLimit
        bookIfPerfectScheduleFound
        suggestOverAvailableKeywords
        suggestOverFreeTime
        suggestOverRecruitingKeywords
        rollingDays
        inclusionDays
      }
      selfScheduleRequests {
        id
        applicationId
        createdAt
        creatorEmployeeId
        deletedAt
        jobStageId
        interviewerEventTemplateId
        candidateEventTemplateId
        candidateEmailTemplateId
        lastUpdatedByEmployeeId
        options {
          inclusionDays
          rollingDays
          advanceNoticeInHours
          candidateCalendarId
          interviewerCalendarId
          location
          zoomHost
          zoomHostUserId
          candidateEmail
          canScheduleOverAvailableKeywords
          canScheduleOverFreeTime
          canScheduleOverRecruitingKeywords
          shouldRespectLoadLimit
        }
        rescheduledAt
        selfScheduleEmailTemplateId
        selfScheduleEmailSentAt
        updatedAt
      }
    }
  }
`;

export default TaskShareDialog;
